/**
 * Support Case Detail -- Aaim-operator (/support/:caseId).
 *
 * platform.aaim.com:9303 (Aaim-operator SPA). The operator works a single case:
 * read the thread, reply, and update status/priority (resolve). Operator-run,
 * tenant-homed model -- see node-5-operator-console-PLAN.md.
 *
 * Audience: platform-admin ONLY. Reads via GET /api/v1/support/cases/{caseId}
 * (+ /messages); the platform-admin queue sees all tenants.
 *
 * Workflow: read the thread, reply, update status/priority (resolve), and
 * (B2.1, 2026-07-10) escalate / transfer against the agent roster. Types are
 * the generated support-platform spec; no view-model interfaces.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Heading,
  BodyText,
  Button,
  Badge,
  Card,
  Textarea,
  Select,
  Alert,
  Dialog,
} from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/support-platform';
import { useAuthStore } from '../../stores/auth';
import { supportPlatformClient } from '../../api/typed-clients';
import { SupportServiceMissingError, SupportServiceNotice, supportFailure } from './SupportServiceNotice';

type SupportCase = components['schemas']['SupportCase'];
type SupportCaseMessage = components['schemas']['SupportCaseMessage'];
type SupportCaseStatus = components['schemas']['SupportCaseStatus'];
type SupportCasePriority = components['schemas']['SupportCasePriority'];
type SupportAgent = components['schemas']['SupportAgent'];

const PLATFORM_ADMIN_ROLES = ['aaim_super_admin', 'platform_admin', 'e2e_test_admin'] as const;

function statusVariant(status: SupportCaseStatus): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (status) {
    case 'open':
    case 'awaiting_platform_admin':
      return 'warning';
    case 'awaiting_user':
    case 'awaiting_fi_input':
      return 'info';
    case 'resolved':
      return 'success';
    case 'closed':
      return 'neutral';
    default:
      return 'neutral';
  }
}

function priorityVariant(priority: SupportCasePriority): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (priority) {
    case 'P1':
      return 'error';
    case 'P2':
      return 'warning';
    case 'P3':
      return 'info';
    case 'P4':
      return 'neutral';
    default:
      return 'neutral';
  }
}

/** Roster label for an agent id; falls back to the truncated id when the roster has no match. */
function agentLabel(agents: SupportAgent[], agentId: string): string {
  const match = agents.find((a) => a.id === agentId);
  return match ? agentOptionLabel(match) : `${agentId.slice(0, 8)}...`;
}

function agentOptionLabel(agent: SupportAgent): string {
  const scope = agent.roleScope ? String(agent.roleScope).toUpperCase() : '';
  const tags =
    Array.isArray(agent.specialtyTags) && agent.specialtyTags.length > 0
      ? ` (${agent.specialtyTags.join(', ')})`
      : '';
  return `${scope} ${String(agent.userId ?? '').slice(0, 8)}...${tags}`;
}

export function SupportCaseDetailPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const user = useAuthStore((s) => s.user);
  const roles = useMemo(
    () => (user?.roles ?? []).map((r) => (typeof r === 'string' ? r.toLowerCase() : r)),
    [user],
  );
  const isPlatformAdmin = PLATFORM_ADMIN_ROLES.some((r) => roles.includes(r));

  const [supportCase, setSupportCase] = useState<SupportCase | null>(null);
  const [messages, setMessages] = useState<SupportCaseMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [serviceMissing, setServiceMissing] = useState<boolean>(false);

  const [replyBody, setReplyBody] = useState<string>('');
  const [replyError, setReplyError] = useState<string | null>(null);
  const [replyPosting, setReplyPosting] = useState<boolean>(false);

  const [statusDraft, setStatusDraft] = useState<string>('');
  const [priorityDraft, setPriorityDraft] = useState<string>('');
  const [updatePosting, setUpdatePosting] = useState<boolean>(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // B2.1 -- escalate / transfer against the agent roster.
  const [agents, setAgents] = useState<SupportAgent[]>([]);
  const [escalateOpen, setEscalateOpen] = useState<boolean>(false);
  const [escalateReason, setEscalateReason] = useState<string>('');
  const [escalatePriority, setEscalatePriority] = useState<string>('');
  const [escalateReassignTo, setEscalateReassignTo] = useState<string>('');
  const [escalatePosting, setEscalatePosting] = useState<boolean>(false);
  const [escalateError, setEscalateError] = useState<string | null>(null);
  const [transferOpen, setTransferOpen] = useState<boolean>(false);
  const [transferTarget, setTransferTarget] = useState<string>('');
  const [transferNote, setTransferNote] = useState<string>('');
  const [transferPosting, setTransferPosting] = useState<boolean>(false);
  const [transferError, setTransferError] = useState<string | null>(null);

  const loadDetail = useCallback(async () => {
    if (!caseId || !isPlatformAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    setServiceMissing(false);
    try {
      const { data, error, response } = await supportPlatformClient.GET('/api/v1/support/cases/{caseId}', {
        params: { path: { caseId } },
      });
      if (error !== undefined || !response.ok) throw supportFailure(response, error, 'Failed to load case');
      setSupportCase(data ?? null);
      setStatusDraft(data?.status ?? '');
      setPriorityDraft(data?.priority ?? '');

      const msg = await supportPlatformClient.GET('/api/v1/support/cases/{caseId}/messages', {
        params: { path: { caseId } },
      });
      if (msg.error !== undefined || !msg.response.ok) throw supportFailure(msg.response, msg.error, 'Failed to load messages');
      setMessages(msg.data?.messages ?? []);
    } catch (err) {
      if (err instanceof SupportServiceMissingError) setServiceMissing(true);
      else setLoadError(err instanceof Error ? err.message : 'Failed to load case');
      setSupportCase(null);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [caseId, isPlatformAdmin]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const loadAgents = useCallback(async () => {
    if (!isPlatformAdmin) return;
    try {
      const { data, error } = await supportPlatformClient.GET('/api/v1/support/agents');
      if (error) throw new Error('Failed to load agents');
      setAgents(data?.agents ?? []);
    } catch {
      // Escalate/transfer selects degrade to empty; the page itself still works.
      setAgents([]);
    }
  }, [isPlatformAdmin]);

  useEffect(() => {
    void loadAgents();
  }, [loadAgents]);

  const handleEscalate = useCallback(async () => {
    if (!caseId) return;
    if (!escalateReason.trim()) {
      setEscalateError('An escalation reason is required.');
      return;
    }
    setEscalatePosting(true);
    setEscalateError(null);
    try {
      const body: {
        reason: string;
        newPriority?: SupportCasePriority;
        reassignToAgentId?: string | null;
      } = { reason: escalateReason.trim() };
      if (escalatePriority) body.newPriority = escalatePriority as SupportCasePriority;
      if (escalateReassignTo) body.reassignToAgentId = escalateReassignTo;
      const { error, response } = await supportPlatformClient.POST(
        '/api/v1/support/cases/{caseId}/escalate',
        { params: { path: { caseId } }, body },
      );
      if (error !== undefined || !response.ok) throw supportFailure(response, error, 'Failed to escalate case');
      setEscalateOpen(false);
      setEscalateReason('');
      setEscalatePriority('');
      setEscalateReassignTo('');
      await loadDetail();
    } catch (err) {
      if (err instanceof SupportServiceMissingError) setServiceMissing(true);
      else setEscalateError(err instanceof Error ? err.message : 'Failed to escalate case');
    } finally {
      setEscalatePosting(false);
    }
  }, [caseId, escalateReason, escalatePriority, escalateReassignTo, loadDetail]);

  const handleTransfer = useCallback(async () => {
    if (!caseId) return;
    if (!transferTarget) {
      setTransferError('Select the agent to transfer to.');
      return;
    }
    setTransferPosting(true);
    setTransferError(null);
    try {
      const body: { targetAgentId: string; note?: string } = { targetAgentId: transferTarget };
      if (transferNote.trim()) body.note = transferNote.trim();
      const { error, response } = await supportPlatformClient.POST(
        '/api/v1/support/cases/{caseId}/transfer',
        { params: { path: { caseId } }, body },
      );
      if (error !== undefined || !response.ok) throw supportFailure(response, error, 'Failed to transfer case');
      setTransferOpen(false);
      setTransferTarget('');
      setTransferNote('');
      await loadDetail();
    } catch (err) {
      if (err instanceof SupportServiceMissingError) setServiceMissing(true);
      else setTransferError(err instanceof Error ? err.message : 'Failed to transfer case');
    } finally {
      setTransferPosting(false);
    }
  }, [caseId, transferTarget, transferNote, loadDetail]);

  const handleReply = useCallback(async () => {
    if (!caseId) return;
    if (!replyBody.trim()) {
      setReplyError('Message body is required.');
      return;
    }
    setReplyPosting(true);
    setReplyError(null);
    try {
      const { error, response } = await supportPlatformClient.POST('/api/v1/support/cases/{caseId}/messages', {
        params: { path: { caseId } },
        body: { body: replyBody },
      });
      if (error !== undefined || !response.ok) throw supportFailure(response, error, 'Failed to post message');
      setReplyBody('');
      await loadDetail();
    } catch (err) {
      if (err instanceof SupportServiceMissingError) setServiceMissing(true);
      else setReplyError(err instanceof Error ? err.message : 'Failed to post message');
    } finally {
      setReplyPosting(false);
    }
  }, [caseId, replyBody, loadDetail]);

  const handleStatusPriorityUpdate = useCallback(async () => {
    if (!caseId || !isPlatformAdmin) return;
    setUpdatePosting(true);
    setUpdateError(null);
    try {
      const body: { status?: SupportCaseStatus; priority?: SupportCasePriority } = {};
      if (statusDraft && statusDraft !== supportCase?.status) {
        body.status = statusDraft as SupportCaseStatus;
      }
      if (priorityDraft && priorityDraft !== supportCase?.priority) {
        body.priority = priorityDraft as SupportCasePriority;
      }
      if (Object.keys(body).length === 0) {
        setUpdateError('No changes to save.');
        setUpdatePosting(false);
        return;
      }
      const { error, response } = await supportPlatformClient.PATCH('/api/v1/support/cases/{caseId}', {
        params: { path: { caseId } },
        body,
      });
      if (error !== undefined || !response.ok) throw supportFailure(response, error, 'Failed to update case');
      await loadDetail();
    } catch (err) {
      if (err instanceof SupportServiceMissingError) setServiceMissing(true);
      else setUpdateError(err instanceof Error ? err.message : 'Failed to update case');
    } finally {
      setUpdatePosting(false);
    }
  }, [caseId, isPlatformAdmin, statusDraft, priorityDraft, supportCase, loadDetail]);

  if (!isPlatformAdmin) {
    return (
      <div className="p-6 space-y-4">
        <Card>
          <div className="p-4">
            <Alert type="error">
              You do not have access to this case. The operator support console is reserved
              for Aaim platform-admin agents.
            </Alert>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <BodyText color="secondary">Loading case...</BodyText>
      </div>
    );
  }

  if (serviceMissing) {
    return (
      <div className="p-6 space-y-4">
        <Link to="/support" className="text-primary hover:underline text-sm">
          &larr; Back to cases
        </Link>
        <SupportServiceNotice />
      </div>
    );
  }

  if (loadError || !supportCase) {
    return (
      <div className="p-6 space-y-4">
        <Link to="/support" className="text-primary hover:underline text-sm">
          &larr; Back to cases
        </Link>
        <Alert type="error">{loadError ?? 'Case not found.'}</Alert>
      </div>
    );
  }

  const statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'awaiting_user', label: 'Awaiting user' },
    { value: 'awaiting_platform_admin', label: 'Awaiting platform admin' },
    { value: 'awaiting_fi_input', label: 'Awaiting FI input' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
  ];

  const priorityOptions = [
    { value: 'P1', label: 'P1 (critical)' },
    { value: 'P2', label: 'P2 (high)' },
    { value: 'P3', label: 'P3 (normal)' },
    { value: 'P4', label: 'P4 (low)' },
  ];

  return (
    <div className="p-6 space-y-4">
      <Link to="/support" className="text-primary hover:underline text-sm">
        &larr; Back to cases
      </Link>

      <div className="flex items-center gap-2">
        <BodyText size="sm" color="secondary">
          {supportCase.reference}
        </BodyText>
        <Badge variant={statusVariant(supportCase.status)} size="sm">
          {supportCase.status}
        </Badge>
        <Badge variant={priorityVariant(supportCase.priority)} size="sm">
          {supportCase.priority}
        </Badge>
        <BodyText size="sm" color="secondary">
          Opened {new Date(supportCase.openedAt).toLocaleString()}
        </BodyText>
      </div>

      <Card>
        <div className="p-4 space-y-3">
          <Heading level={3}>Case</Heading>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-tertiary">Category</dt>
              <dd className="text-primary">{supportCase.category}</dd>
            </div>
            <div>
              <dt className="text-tertiary">Channel</dt>
              <dd className="text-primary">{supportCase.sourceChannel}</dd>
            </div>
            <div>
              <dt className="text-tertiary">Homed tenant</dt>
              <dd className="text-primary font-mono text-xs">{supportCase.tenantId}</dd>
            </div>
            {supportCase.consumerUserId && (
              <div>
                <dt className="text-tertiary">Consumer</dt>
                <dd className="text-primary font-mono text-xs">{supportCase.consumerUserId}</dd>
              </div>
            )}
            {supportCase.assignedPlatformAdminAgentId && (
              <div>
                <dt className="text-tertiary">Assigned agent</dt>
                <dd className="text-primary font-mono text-xs">
                  {supportCase.assignedPlatformAdminAgentId}
                </dd>
              </div>
            )}
            {supportCase.slaTargetAt && (
              <div>
                <dt className="text-tertiary">SLA target</dt>
                <dd className="text-primary">
                  {new Date(supportCase.slaTargetAt).toLocaleString()}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </Card>

      <Card>
        <div className="p-4 space-y-3">
          <Heading level={3}>Status &amp; priority</Heading>
          {updateError && <Alert type="error">{updateError}</Alert>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Select
              label="Status"
              options={statusOptions}
              value={statusDraft}
              onChange={(e) => setStatusDraft(e.target.value)}
            />
            <Select
              label="Priority"
              options={priorityOptions}
              value={priorityDraft}
              onChange={(e) => setPriorityDraft(e.target.value)}
            />
          </div>
          <Button variant="primary" onClick={handleStatusPriorityUpdate} disabled={updatePosting}>
            {updatePosting ? 'Saving...' : 'Save changes'}
          </Button>
        </div>
      </Card>

      <Card>
        <div className="p-4 space-y-3">
          <Heading level={3}>Assignment &amp; escalation</Heading>
          <BodyText size="sm" color="secondary">
            {supportCase.assignedPlatformAdminAgentId
              ? `Assigned to agent ${agentLabel(agents, supportCase.assignedPlatformAdminAgentId)}.`
              : 'Unassigned. New cases auto-assign when an available agent has capacity.'}
            {supportCase.escalationCount ? ` Escalated ${supportCase.escalationCount}x.` : ''}
          </BodyText>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setTransferOpen(true)}>
              Transfer
            </Button>
            <Button variant="secondary" onClick={() => setEscalateOpen(true)}>
              Escalate
            </Button>
          </div>
        </div>
      </Card>

      <Dialog
        open={transferOpen}
        onOpenChange={(o) => {
          if (!o) {
            setTransferOpen(false);
            setTransferError(null);
          }
        }}
        title="Transfer case"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setTransferOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleTransfer}
              disabled={transferPosting || !transferTarget}
            >
              {transferPosting ? 'Transferring...' : 'Transfer'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {transferError && <Alert type="error">{transferError}</Alert>}
          {agents.length === 0 ? (
            <Alert type="warning">
              No agents on the roster. Add agents under Support &gt; Agents first.
            </Alert>
          ) : (
            <Select
              label="Transfer to"
              options={[
                { value: '', label: 'Select an agent...' },
                ...agents.map((a) => ({ value: a.id ?? '', label: agentOptionLabel(a) })),
              ]}
              value={transferTarget}
              onChange={(e) => setTransferTarget(e.target.value)}
            />
          )}
          <Textarea
            label="Note (optional)"
            value={transferNote}
            onChange={(v) => setTransferNote(v)}
            rows={2}
            placeholder="Context for the receiving agent..."
          />
        </div>
      </Dialog>

      <Dialog
        open={escalateOpen}
        onOpenChange={(o) => {
          if (!o) {
            setEscalateOpen(false);
            setEscalateError(null);
          }
        }}
        title="Escalate case"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEscalateOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleEscalate}
              disabled={escalatePosting || !escalateReason.trim()}
            >
              {escalatePosting ? 'Escalating...' : 'Escalate'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {escalateError && <Alert type="error">{escalateError}</Alert>}
          <Textarea
            label="Reason"
            value={escalateReason}
            onChange={(v) => setEscalateReason(v)}
            rows={3}
            required
            placeholder="Why this case needs escalation..."
          />
          <Select
            label="New priority (optional)"
            options={[
              { value: '', label: 'Keep current priority' },
              { value: 'P1', label: 'P1 (critical)' },
              { value: 'P2', label: 'P2 (high)' },
              { value: 'P3', label: 'P3 (normal)' },
              { value: 'P4', label: 'P4 (low)' },
            ]}
            value={escalatePriority}
            onChange={(e) => setEscalatePriority(e.target.value)}
          />
          <Select
            label="Reassign to (optional)"
            options={[
              { value: '', label: 'Keep current assignee' },
              ...agents.map((a) => ({ value: a.id ?? '', label: agentOptionLabel(a) })),
            ]}
            value={escalateReassignTo}
            onChange={(e) => setEscalateReassignTo(e.target.value)}
          />
        </div>
      </Dialog>

      <Card>
        <div className="p-4 space-y-3">
          <Heading level={3}>Messages</Heading>
          {messages.length === 0 ? (
            <BodyText size="sm" color="secondary">
              No messages on this case yet.
            </BodyText>
          ) : (
            <ul className="space-y-3">
              {messages.map((m) => (
                <li key={m.id} className="border-b border-recessed pb-3 last:border-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="neutral" size="sm">
                      {m.authorType}
                    </Badge>
                    <span className="text-xs text-tertiary">
                      {new Date(m.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm text-primary whitespace-pre-wrap">{m.body}</div>
                </li>
              ))}
            </ul>
          )}

          <div className="pt-3 border-t border-recessed space-y-2">
            {replyError && <Alert type="error">{replyError}</Alert>}
            <Textarea
              label="Reply"
              value={replyBody}
              onChange={(v) => setReplyBody(v)}
              rows={4}
              required
              placeholder="Write your reply..."
            />
            <Button variant="primary" onClick={handleReply} disabled={replyPosting}>
              {replyPosting ? 'Sending...' : 'Send reply'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
